import type { ContentItem, ToolKind } from './types.js';

/**
 * The name a tool call gets when its frame carried none. It names nothing, so
 * no row should ever lead with it — see {@link ./acp-title.ts}.
 */
export const UNTITLED_TOOL_CALL = 'unknown';

export function parseToolCallContent(raw: unknown[]): ContentItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry: unknown) => {
    const item = entry as Record<string, unknown>;
    if (item.type === 'diff') {
      return {
        type: 'diff' as const,
        diff: {
          path: (item.path as string) ?? '',
          oldText: (item.oldText as string | null) ?? null,
          newText: (item.newText as string) ?? '',
        },
      };
    }
    if (item.type === 'terminal') {
      return {
        type: 'terminal' as const,
        terminal: { terminalId: (item.terminalId as string) ?? '' },
      };
    }
    const content = item.content as { type?: string; text?: string } | undefined;
    return {
      type: 'content' as const,
      text: content?.text ?? '',
    };
  });
}

export function extractOutputText(
  contentItems: ContentItem[],
  rawOutput: unknown,
): string | undefined {
  const texts = contentItems.filter((c) => c.type === 'content' && c.text).map((c) => c.text!);
  if (texts.length > 0) return texts.join('\n');
  if (rawOutput && typeof rawOutput === 'object') {
    const ro = rawOutput as Record<string, unknown>;
    return (ro.stdout as string) ?? (ro.error as string) ?? (ro.output as string) ?? undefined;
  }
  return undefined;
}

/** Compute elapsed duration in seconds, clamped to zero. */
export function durationSecs(start: number, end: number, decimals: number = 1): number {
  const factor = 10 ** decimals;
  return Math.round((Math.max(0, end - start) / 1000) * factor) / factor;
}

let blockIdCounter = 0;
export function nextBlockId(prefix: string): string {
  return `${prefix}-${++blockIdCounter}`;
}

export function inferToolKind(toolName: string): ToolKind {
  const name = toolName.toLowerCase();
  if (
    name.includes('search') ||
    name.includes('grep') ||
    name.includes('glob') ||
    name.includes('find')
  )
    return 'search';
  if (name.includes('read') || name.includes('cat')) return 'read';
  if (
    name.includes('edit') ||
    name.includes('write') ||
    name.includes('replace') ||
    name.includes('notebook')
  )
    return 'edit';
  if (
    name.includes('bash') ||
    name.includes('exec') ||
    name.includes('terminal') ||
    name.includes('shell')
  )
    return 'execute';
  if (name.includes('delete') || name.includes('remove')) return 'delete';
  if (name.includes('move') || name.includes('rename')) return 'move';
  if (name.includes('fetch') || name.includes('web')) return 'fetch';
  if (name.includes('task') || name.includes('agent') || name.includes('think')) return 'think';
  if (name.includes('switch') && name.includes('mode')) return 'switch_mode';
  return 'other';
}
